import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_TRAFFIC_EXCLUSIONS_GRID } from '../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { GzContentControlTrafficExclusionsGridDataProvider } from './GzContentControlTrafficExclusionsGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { MAX_LENGTH, validateDuplicates, validateEntityByType } from '../utils/validators';
import { GRID_ITEM_FORM_ID } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { GridCellEditWebComponent } from '../../../../../../../grid/uiElements/gridCellEdit';
import {
  ContentControlTrafficExclusion,
  ContentControlTrafficExclusionType,
} from '@nirvana/shared/dtos/types/policies/common';
import { CustomizableValidator } from '../../../../../../../ui/gzForm/validators/customizable.validator';
import { MaxLengthValidator } from '../../../../../../../ui/gzForm/validators/max-length.validator';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_TRAFFIC_EXCLUSIONS_GRID)
export class GzContentControlTrafficExclusionsGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ContentControlTrafficExclusion,
  ContentControlTrafficExclusion,
  GzContentControlTrafficExclusionsGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;

  protected translations = {
    editRow: translateFn('Edit exclusion'),
    deleteRow: translateFn('Delete'),
    emptyState: translateFn('No exclusions'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  protected getDataProvider(): GzContentControlTrafficExclusionsGridDataProvider {
    return new GzContentControlTrafficExclusionsGridDataProvider(this.initialGridItems);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button
        id="delete-button"
        type="outline"
        disabled
        color="negative"
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete exclusions?'),
          translateFn('You are about to delete the selected exclusions. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  protected renderGridItemForm(): TemplateResult {
    return html` <gz-policy-panel-content-control-traffic-exclusion-item
      id="${GRID_ITEM_FORM_ID}"
    ></gz-policy-panel-content-control-traffic-exclusion-item>`;
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: ContentControlTrafficExclusion): ValidationResult {
    // check for duplicates
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzContentControlTrafficExclusionsGridDataProvider).items
    );
  }

  protected onEditRowEnabled(event: CustomEvent) {
    // set validators on edited row
    const { rowData } = event.detail;

    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.ENTITY, [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) =>
        validateEntityByType(rowData.item.type as ContentControlTrafficExclusionType, value)
      ),
      MaxLengthValidator(
        MAX_LENGTH,
        translateFn('The excluded item must have a maximum of {maxLength} characters.', { maxLength: MAX_LENGTH })
      ),
    ]);
    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.REMARKS, [
      MaxLengthValidator(
        MAX_LENGTH,
        translateFn('The description must have a maximum of {maxLength} characters.', { maxLength: MAX_LENGTH })
      ),
    ]);
  }

  protected isEditedRowChanged(
    oldItem: ContentControlTrafficExclusion,
    newItem: ContentControlTrafficExclusion
  ): boolean {
    return oldItem.entity !== newItem.entity || oldItem.remarks !== newItem.remarks;
  }

  protected validateGridItemBeforeEdit(
    oldItem: ContentControlTrafficExclusion,
    newItem: ContentControlTrafficExclusion
  ): ValidationResult {
    // skip if entity is not changed
    if (newItem.entity === oldItem.entity) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected revertGridCellEditValue(
    gridCellEdit: GridCellEditWebComponent,
    oldItem: ContentControlTrafficExclusion
  ): void {
    if (gridCellEdit.id === ColumnKeys.ENTITY) {
      gridCellEdit.value = oldItem.entity;
    }
  }

  public addExclusionItem(item: ContentControlTrafficExclusion): void {
    const validationResult = this.validateGridItemBeforeAdd(item);
    if (!validationResult.valid) {
      return;
    }

    // add new item to the grid
    this.addGridItem(item);
    this.onGridItemsChange();
  }
}
